import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export interface HowToPlayStep { title: string; description: string }
export interface PrizeTier { match: string; prize: string; odds: string }
export interface MultiplierRow { match: string; base: string; x2: string; x3: string; x4: string; x5: string; x10: string }
export interface MultiplierOdds { multiplier: string; odds: string }
export interface DrawScheduleRow { day: string; time: string }
export interface QuickFact { label: string; value: string }
export interface FAQ { question: string; answer: string }

export interface HowToPlayPageData {
  meta: { title: string; description: string; canonical: string };
  gameName: string;
  gameSlug: string;
  heroDescription: string;
  keyFacts: string[];
  steps: HowToPlayStep[];
  prizeTiersIntro: string;
  prizeTiersNote?: string;
  prizeTiers: PrizeTier[];
  multiplierTitle?: string;
  multiplierIntro?: string;
  multiplierNote?: string;
  multiplierRows?: MultiplierRow[];
  multiplierOdds?: MultiplierOdds[];
  multiplierOddsNote?: string;
  jackpotTitle: string;
  jackpotDescription: string;
  jackpotOptions: { label: string; description: string }[];
  jackpotFootnote: string;
  drawSchedule: DrawScheduleRow[];
  drawScheduleNote: string;
  claimRules: string[];
  claimNote: string;
  taxTitle: string;
  taxSections: { heading: string; text: string }[];
  taxCta: string;
  quickFacts: QuickFact[];
  faqs: FAQ[];
  disclaimer: string;
}

export function HowToPlayPage(props: HowToPlayPageData) {
  const {
    meta, gameName, gameSlug, heroDescription, keyFacts, steps, prizeTiersIntro, prizeTiersNote,
    prizeTiers, multiplierTitle, multiplierIntro, multiplierNote, multiplierRows,
    multiplierOdds, multiplierOddsNote, jackpotTitle, jackpotDescription, jackpotOptions, jackpotFootnote,
    drawSchedule, drawScheduleNote, claimRules, claimNote,
    taxTitle, taxSections, taxCta, quickFacts, faqs, disclaimer,
  } = props;

  return (
    <Layout>
      <SEOHead title={meta.title} description={meta.description} />
      <div className="container mx-auto max-w-4xl px-4 py-8 space-y-12">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/games/$gameSlug" params={{ gameSlug }}>{gameName}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>How to Play</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <section>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 font-display">
            How to Play {gameName}: Complete Guide to Rules, Prizes &amp; Odds
          </h1>
          <p className="text-lg text-muted-foreground mb-4">{heroDescription}</p>
          <div className="flex flex-wrap gap-2">
            {keyFacts.map((fact) => (
              <Badge key={fact} variant="secondary" className="text-sm">{fact}</Badge>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-6 font-display">How to Play {gameName}: Step by Step</h2>
          <ol className="space-y-4">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold">{step.title}</p>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">{gameName} Prize Tiers &amp; Odds</h2>
          <p className="text-muted-foreground mb-4">{prizeTiersIntro}</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Match</TableHead>
                <TableHead>Prize</TableHead>
                <TableHead>Odds of Winning</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prizeTiers.map((tier) => (
                <TableRow key={tier.match}>
                  <TableCell className="font-medium">{tier.match}</TableCell>
                  <TableCell>{tier.prize}</TableCell>
                  <TableCell>{tier.odds}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {prizeTiersNote && <p className="text-sm text-muted-foreground mt-3">{prizeTiersNote}</p>}
        </section>

        {multiplierTitle && multiplierRows && multiplierRows.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">{multiplierTitle}</h2>
          <p className="text-muted-foreground mb-4">{multiplierIntro}</p>
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Match</TableHead>
                  <TableHead>Base Prize</TableHead>
                  <TableHead>2x</TableHead>
                  <TableHead>3x</TableHead>
                  <TableHead>4x</TableHead>
                  <TableHead>5x</TableHead>
                  <TableHead>10x</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {multiplierRows.map((row) => (
                  <TableRow key={row.match}>
                    <TableCell className="font-medium whitespace-nowrap">{row.match}</TableCell>
                    <TableCell>{row.base}</TableCell>
                    <TableCell>{row.x2}</TableCell>
                    <TableCell>{row.x3}</TableCell>
                    <TableCell>{row.x4}</TableCell>
                    <TableCell>{row.x5}</TableCell>
                    <TableCell>{row.x10}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {multiplierNote && <p className="text-sm text-muted-foreground mt-3">{multiplierNote}</p>}

          {multiplierOdds && multiplierOdds.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Odds of Each Multiplier</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Multiplier</TableHead>
                    <TableHead>Approximate Odds</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {multiplierOdds.map((row) => (
                    <TableRow key={row.multiplier}>
                      <TableCell className="font-medium">{row.multiplier}</TableCell>
                      <TableCell>{row.odds}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {multiplierOddsNote && <p className="text-sm text-muted-foreground mt-3">{multiplierOddsNote}</p>}
            </div>
          )}
        </section>
        )}

        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">{jackpotTitle}</h2>
          <p className="text-muted-foreground mb-4">{jackpotDescription}</p>
          <ul className="space-y-3 mb-4">
            {jackpotOptions.map((opt) => (
              <li key={opt.label}>
                <span className="font-semibold">{opt.label}:</span>{" "}
                <span className="text-muted-foreground">{opt.description}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground">{jackpotFootnote}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">{gameName} Drawing Schedule</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>Drawing Time (Eastern)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drawSchedule.map((row) => (
                <TableRow key={row.day}>
                  <TableCell className="font-medium">{row.day}</TableCell>
                  <TableCell>{row.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-sm text-muted-foreground mt-3">{drawScheduleNote}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">How to Claim a {gameName} Prize</h2>
          <ul className="space-y-2 list-disc list-inside text-muted-foreground">
            {claimRules.map((rule, i) => (<li key={i}>{rule}</li>))}
          </ul>
          <p className="text-sm text-muted-foreground mt-3">{claimNote}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">{taxTitle}</h2>
          {taxSections.map((s) => (
            <div key={s.heading} className="mb-4">
              <h3 className="text-lg font-semibold mb-1">{s.heading}</h3>
              <p className="text-muted-foreground">{s.text}</p>
            </div>
          ))}
          <p className="text-muted-foreground">
            <Link to="/tools/tax-calculator" className="text-primary underline hover:no-underline">{taxCta}</Link>
          </p>
        </section>

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold mb-4 font-display">{gameName} Quick Facts</h2>
            <Table>
              <TableBody>
                {quickFacts.map((fact) => (
                  <TableRow key={fact.label}>
                    <TableCell className="font-semibold w-1/3">{fact.label}</TableCell>
                    <TableCell>{fact.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <section>
          <h2 className="text-2xl font-bold mb-4 font-display">Frequently Asked Questions</h2>
          <Accordion type="multiple" className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <p className="text-xs text-muted-foreground border-t pt-6">{disclaimer}</p>
      </div>
    </Layout>
  );
}